// src/components/gavion/integrations/api/supabaseM365Api.ts
// GAVION batch 3 (Task 22): M365Api over Supabase. Edge functions (gavion-integration-connect,
// gavion-graph-send/-proxy/-calendar, gavion-teams-notify) and the Task 19 RPCs. Every failure is an
// M365ApiError carrying a stable code only (plus Retry-After seconds when the server sent one): a raw
// HTTP status never escapes, so a function's 401/403 can never reach ra-core's checkError and log the
// user out. The proxy is read with fetch, the JWT in the Authorization header (never in a URL), and an
// attachment comes back as a blob object URL.
import {
  M365ApiError,
  type M365Api,
  type MessageBodyView,
  type MicrosoftStatus,
  type StoredAttachmentStatus,
  type StoredMessageView,
  type TeamsConfig,
  type DemoResult,
} from "./types";

type InvokeResult = { data: unknown; error: unknown };
export type SupabaseLike = {
  functions: {
    invoke(
      name: string,
      o: { method: "GET" | "POST"; body?: unknown },
    ): Promise<InvokeResult>;
  };
  rpc(
    name: string,
    args?: Record<string, unknown>,
  ): PromiseLike<{
    data: unknown;
    error: { code?: string; message?: string } | null;
  }>;
  from(table: string): {
    select(c: string): {
      eq(
        c: string,
        v: unknown,
      ): { maybeSingle(): PromiseLike<{ data: unknown; error: unknown }> };
    };
  };
  auth: {
    getSession(): Promise<{
      data: { session: { access_token: string; user: { id: string } } | null };
    }>;
  };
  /** GAVION full-email: signed download URLs of stored attachments (optional in older test doubles). */
  storage?: {
    from(bucket: string): {
      createSignedUrl(
        path: string,
        expiresIn: number,
        o?: { download?: string | boolean },
      ): Promise<{ data: { signedUrl: string } | null; error: unknown }>;
    };
  };
};
export type SupabaseM365Options = {
  /** `<SUPABASE_URL>/functions/v1`; defaults to VITE_SUPABASE_URL. */
  functionsUrl?: string;
  /** Publishable key sent as `apikey`; defaults to VITE_SB_PUBLISHABLE_KEY. */
  publishableKey?: string;
};

const SAFE = /^[a-z0-9_]{1,40}$/;
const code = (v: unknown) =>
  typeof v === "string" && SAFE.test(v) ? v : "unknown";
const MAX_RETRY_AFTER_S = 3600;

/** Retry-After as whole seconds (delta-seconds or HTTP-date); undefined when absent or unreadable. */
export function retryAfterSeconds(
  h: string | null,
  now = Date.now(),
): number | undefined {
  if (!h) return undefined;
  const v = h.trim();
  let s: number;
  if (/^\d{1,10}$/.test(v)) s = Number(v);
  else {
    const t = Date.parse(v);
    if (Number.isNaN(t)) return undefined;
    s = Math.max(0, Math.ceil((t - now) / 1000));
  }
  return Math.min(s, MAX_RETRY_AFTER_S);
}

/** A non-2xx function Response → M365ApiError(code, { suppressed }?, { retryAfter }?). */
async function errorFrom(res: Response): Promise<M365ApiError> {
  const b = (await res
    .clone()
    .json()
    .catch(() => ({}))) as { error?: unknown; suppressed?: unknown } | null;
  const suppressed = Array.isArray(b?.suppressed)
    ? b.suppressed.filter((x): x is string => typeof x === "string")
    : null;
  const retryAfter = retryAfterSeconds(res.headers.get("retry-after"));
  return new M365ApiError(
    code(b?.error),
    suppressed ? { suppressed } : undefined,
    retryAfter === undefined ? undefined : { retryAfter },
  );
}

async function call<T>(
  c: SupabaseLike,
  name: string,
  body: unknown,
): Promise<T> {
  let r: InvokeResult;
  try {
    r = await c.functions.invoke(name, { method: "POST", body });
  } catch {
    throw new M365ApiError("graph_unavailable");
  }
  if (!r.error) return r.data as T;
  const res = (r.error as { context?: unknown } | null)?.context;
  // FunctionsFetchError (network, CORS): its context is the fetch error, not a Response.
  if (!(res instanceof Response)) throw new M365ApiError("graph_unavailable");
  throw await errorFrom(res);
}

async function rpc<T>(
  c: SupabaseLike,
  name: string,
  args: Record<string, unknown>,
): Promise<T> {
  let r: Awaited<ReturnType<SupabaseLike["rpc"]>>;
  try {
    r = await c.rpc(name, args);
  } catch {
    throw new M365ApiError("graph_unavailable");
  }
  const { data, error } = r;
  if (error)
    throw new M365ApiError(
      error.code === "P0001"
        ? code(error.message)
        : error.code === "42501"
          ? "forbidden"
          : "unknown",
    );
  return data as T;
}

const str = (v: unknown) => (typeof v === "string" ? v : null);
const toTeams = (
  t: Record<string, unknown> | null | undefined,
): TeamsConfig => ({
  teamId: str(t?.team_id),
  teamName: str(t?.team_name),
  channelId: str(t?.channel_id),
  channelName: str(t?.channel_name),
  posterSalesId:
    typeof t?.poster_sales_id === "number" ? t.poster_sales_id : null,
  webhookConfigured: t?.webhook_configured === true,
});

// GAVION full-email
const STORED_STATUSES = new Set([
  "pending",
  "fetching",
  "stored",
  "partial",
  "gone",
  "private",
  "failed",
  "unreachable",
]);
const STORED_COLUMNS =
  "status, body_type, body_html, body_text, truncated, fetched_at, email_message_attachments(id, name, size_bytes, status, storage_path)";
const toStored = (r: Record<string, unknown>): StoredMessageView | null => {
  const status = String(r.status ?? "");
  if (!STORED_STATUSES.has(status)) return null;
  const atts = Array.isArray(r.email_message_attachments)
    ? (r.email_message_attachments as Record<string, unknown>[])
    : [];
  return {
    status: status as StoredMessageView["status"],
    bodyType:
      r.body_type === "html" || r.body_type === "text" ? r.body_type : null,
    bodyHtml: typeof r.body_html === "string" ? r.body_html : null,
    bodyText: typeof r.body_text === "string" ? r.body_text : null,
    truncated: r.truncated === true,
    fetchedAt: typeof r.fetched_at === "string" ? r.fetched_at : null,
    attachments: atts
      .map((a) => ({
        id: Number(a.id),
        name: String(a.name ?? ""),
        sizeBytes: Number(a.size_bytes ?? 0),
        status: String(a.status) as StoredAttachmentStatus,
        storagePath: typeof a.storage_path === "string" ? a.storage_path : null,
      }))
      .sort((x, y) => x.id - y.id),
  };
};

export function createSupabaseM365Api(
  c: SupabaseLike,
  opts: SupabaseM365Options = {},
): M365Api {
  const functionsUrl =
    opts.functionsUrl ??
    `${import.meta.env.VITE_SUPABASE_URL ?? ""}/functions/v1`;
  const publishableKey =
    opts.publishableKey ?? import.meta.env.VITE_SB_PUBLISHABLE_KEY ?? "";

  /** GET gavion-graph-proxy with the user's JWT in the Authorization header. */
  async function proxyGet(qs: string): Promise<Response> {
    const token = (await c.auth.getSession()).data.session?.access_token;
    if (!token) throw new M365ApiError("unauthorized");
    const res = await fetch(`${functionsUrl}/gavion-graph-proxy?${qs}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}`, apikey: publishableKey },
    }).catch(() => null);
    if (!res) throw new M365ApiError("graph_unavailable");
    if (!res.ok) throw await errorFrom(res);
    return res;
  }

  return {
    microsoftStatus: () =>
      call<MicrosoftStatus>(
        c,
        "gavion-integration-connect/microsoft/status",
        {},
      ),
    startMicrosoftConnect: () =>
      call<{ url: string }>(
        c,
        "gavion-integration-connect/microsoft/start",
        {},
      ),
    storeMicrosoftTokens: (t) =>
      call<MicrosoftStatus>(c, "gavion-integration-connect/microsoft/store", {
        provider_token: t.providerToken,
        provider_refresh_token: t.providerRefreshToken,
      }),
    disconnectMicrosoft: async () => {
      await call(c, "gavion-integration-connect/microsoft/disconnect", {});
    },
    async teamsConfig() {
      const { data, error } = await c
        .from("configuration")
        .select("config")
        .eq("id", 1)
        .maybeSingle();
      if (error) throw new M365ApiError("graph_unavailable");
      return toTeams(
        (
          data as {
            config?: { gavion?: { teams?: Record<string, unknown> } };
          } | null
        )?.config?.gavion?.teams,
      );
    },
    listTeams: async () =>
      (
        await call<{ teams: { id: string; name: string }[] }>(
          c,
          "gavion-teams-notify",
          { action: "list_teams" },
        )
      ).teams ?? [],
    listChannels: async (teamId) =>
      (
        await call<{ channels: { id: string; name: string }[] }>(
          c,
          "gavion-teams-notify",
          { action: "list_channels", team_id: teamId },
        )
      ).channels ?? [],
    saveTeamsChannel: async (i) =>
      toTeams(
        await rpc(c, "set_teams_channel", {
          p_team_id: i.teamId,
          p_team_name: i.teamName,
          p_channel_id: i.channelId,
          p_channel_name: i.channelName,
        }),
      ),
    setTeamsWebhook: async (url) =>
      toTeams(await rpc(c, "set_teams_webhook", { p_url: url })),
    async getSignature() {
      const uid = (await c.auth.getSession()).data.session?.user.id;
      if (!uid) return "";
      const { data, error } = await c
        .from("sales")
        .select("prefs")
        .eq("user_id", uid)
        .maybeSingle();
      // Never "" on a failed read: the editor would then overwrite the real signature.
      if (error) throw new M365ApiError("graph_unavailable");
      const html = (
        data as { prefs?: { email_signature_html?: unknown } } | null
      )?.prefs?.email_signature_html;
      return typeof html === "string" ? html : "";
    },
    saveSignature: async (html) => {
      await rpc(c, "set_my_email_signature", { p_html: html });
    },
    async sendEmail(i) {
      const r = await call<{ message_id: number; duplicate?: boolean }>(
        c,
        "gavion-graph-send",
        {
          idempotency_key: i.idempotencyKey,
          to: i.to,
          cc: i.cc,
          subject: i.subject,
          html: i.html,
          company_id: i.companyId ?? null,
          contact_id: i.contactId ?? null,
          deal_id: i.dealId ?? null,
          template_id: i.templateId ?? null,
          ai_draft_id: i.aiDraftId ?? null,
          ai_draft_edited: i.aiDraftEdited ?? false,
          ai_draft_on_demand: i.aiDraftOnDemand ?? false,
          attachments: i.attachments.map(
            ({ name, contentType, contentBase64 }) => ({
              name,
              contentType,
              contentBase64,
            }),
          ),
        },
      );
      return { messageId: r.message_id, duplicate: r.duplicate };
    },
    messageBody: async (id) =>
      (await (
        await proxyGet(`id=${encodeURIComponent(String(id))}`)
      ).json()) as MessageBodyView,
    attachmentUrl: async (id, attId) =>
      URL.createObjectURL(
        await (
          await proxyGet(
            `id=${encodeURIComponent(String(id))}&attachment_id=${encodeURIComponent(attId)}`,
          )
        ).blob(),
      ),
    // GAVION full-email: RLS decides; any error (no row, not allowed, older schema) falls back to the live viewer
    async storedMessage(emailMessageId) {
      const { data, error } = await c
        .from("email_message_contents")
        .select(STORED_COLUMNS)
        .eq("email_message_id", emailMessageId)
        .maybeSingle();
      if (error || !data) return null;
      return toStored(data as Record<string, unknown>);
    },
    async storedAttachmentUrl(path, name) {
      const r = await c.storage
        ?.from("email-attachments")
        .createSignedUrl(path, 60, { download: name })
        .catch(() => null);
      if (!r || r.error || !r.data?.signedUrl)
        throw new M365ApiError("attachment_unavailable");
      return r.data.signedUrl;
    },
    bookDemo: (i) =>
      call<DemoResult>(c, "gavion-graph-calendar", {
        action: "book",
        deal_id: i.dealId,
        start_at: i.startAt,
        duration_min: i.durationMin,
        attendee_email: i.attendeeEmail ?? null,
        idempotency_key: i.idempotencyKey,
      }),
    rescheduleDemo: (i) =>
      call<DemoResult>(c, "gavion-graph-calendar", {
        action: "reschedule",
        deal_id: i.dealId,
        start_at: i.startAt,
        duration_min: i.durationMin,
      }),
    cancelDemo: async (i) => {
      await call(c, "gavion-graph-calendar", {
        action: "cancel",
        deal_id: i.dealId,
        comment: i.comment ?? null,
      });
    },
    takeoverDemo: (i) =>
      call<DemoResult>(c, "gavion-graph-calendar", {
        action: "takeover",
        deal_id: i.dealId,
        start_at: i.startAt,
        duration_min: i.durationMin,
        idempotency_key: i.idempotencyKey,
      }),
  };
}
